import type { AdminAccountIdentitySnapshot } from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../locale/admin-dashboard-locale";

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return locale === "ko" ? ko : en;
}

function toLabel(value: string | null | undefined): string {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : "-";
}

function formatVerifiedSuffix(
  value: boolean | undefined,
  locale: AdminDashboardLocale,
): string | null {
  if (value === true) return text(locale, "verified", "인증됨");
  if (value === false) return text(locale, "unverified", "미인증");
  return null;
}

export function formatAdminAccountIdentityLabel(
  identity: AdminAccountIdentitySnapshot | undefined,
  locale: AdminDashboardLocale,
): string {
  if (!identity) return "-";

  const parts: string[] = [];
  const displayName = toLabel(identity.displayName);
  const accountEmail = toLabel(identity.accountEmail);
  const provider = toLabel(identity.provider);
  const verification = formatVerifiedSuffix(identity.emailVerified, locale);

  if (displayName !== "-") parts.push(displayName);
  if (accountEmail !== "-") {
    parts.push(verification ? `${accountEmail} (${verification})` : accountEmail);
  }
  if (provider !== "-") parts.push(provider);

  return parts.length > 0 ? parts.join(" / ") : "-";
}

export function formatAdminHashedAccountCueLabel(
  hashedIdentifier: string | null | undefined,
  accountCueLabel: string | null | undefined,
): string {
  const hashLabel = toLabel(hashedIdentifier);
  const cueLabel = toLabel(accountCueLabel);

  if (hashLabel === "-" && cueLabel === "-") {
    return "- / -";
  }

  if (hashLabel === "-") {
    return `- / ${cueLabel}`;
  }

  if (cueLabel === "-") {
    return `${hashLabel} / -`;
  }

  return `${hashLabel} / ${cueLabel}`;
}

export function formatAdminProviderIdentityLabel(
  identity: AdminAccountIdentitySnapshot | undefined,
  locale: AdminDashboardLocale,
): string {
  if (!identity) return "-";

  const parts: string[] = [];
  const provider = toLabel(identity.provider);
  const providerEmail = toLabel(identity.providerEmail);
  const providerDisplayName = toLabel(identity.providerDisplayName);
  const verification = formatVerifiedSuffix(identity.providerEmailVerified, locale);

  if (provider !== "-") parts.push(provider);
  if (providerEmail !== "-") {
    parts.push(verification ? `${providerEmail} (${verification})` : providerEmail);
  }
  if (providerDisplayName !== "-") parts.push(providerDisplayName);

  return parts.length > 0 ? parts.join(" / ") : "-";
}

export function formatAdminLinkedProviderCountLabel(
  identity: AdminAccountIdentitySnapshot | undefined,
  locale: AdminDashboardLocale,
): string {
  if (identity?.linkedProviderCount == null) {
    return "-";
  }

  return locale === "ko"
    ? `${identity.linkedProviderCount}개 연결`
    : `${identity.linkedProviderCount} linked`;
}
